import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { EidProvider, Country, Language } from '@/types';
import { CallInitiator, AuthMethod } from '@/teleid';
import { Page } from '@/common';
import { client } from '@/client';

export type StartTeleSignRequest = {
  provider: EidProvider;
  country: Country;
  text: string;
  agreementEmail: string;
  orgNumber?: string;
  language?: Language;
} & (
  | {
      method: AuthMethod.SSN;
      ssn: string;
      callInitiator: CallInitiator;
    }
  | {
      method: AuthMethod.SMS;
      phone: string;
    }
  | {
      method: AuthMethod.EMAIL;
      email: string;
    }
  | {
      method: AuthMethod.CHAT;
    }
);

export type TeleSignLogResponse = {
  id: string;
  ssn: string;
  country: Country;
  dateTime: number; // UNIX timestamp (seconds)
  autodeleteDate: string; // yyyy-MM-dd
  text: string;
  email: string;
  agentName: string;
  bankIdToken: string;
  endUserName: string;
  certStartDate: number; // UNIX timestamp (seconds)
  bankIdNumber: string;
  address: string;
  phone: string;
  hash: string;
  companySignatoryOrgNumber: string;
  companySignatoryText: string;
};

export type TeleSignDeliveryStatus = 'PENDING' | 'FAILED' | 'DELIVERED';

export type TeleSignStatusResponse = {
  id: string;
  method: AuthMethod;
  provider: EidProvider;
} & (
  | {
      status: 'PENDING';
      deliveryStatus?: TeleSignDeliveryStatus;
      bindingMessage?: string;
      url?: string;
    }
  | {
      status: 'COMPLETED';
      name: string;
      ssn?: string;
      country: Country;
      certStartDate?: string; // yyyy-MM-dd
      companySignatoryText?: string;
      bankName?: string;
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export type TeleSignSigningStatusResponse = {
  id: string;
  method: AuthMethod;
  provider: EidProvider;
  language: Language;
  companyName: string;
  companyLogo: string;
} & (
  | {
      status: 'PENDING';
      agentName?: string;
      displayText?: string;
      signingText: string;
      hash: string;
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export async function getTeleSignLogs(
  params?: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: 'id' | 'dateTime' | 'ssn' | 'endUserName' | 'agentName';
    sortDirection?: 'ascending' | 'descending';
    fromDate?: Date;
    toDate?: Date;
    ssn?: string;
  },
  signal?: AbortSignal,
): Promise<Page<TeleSignLogResponse>> {
  return client
    .get<never, AxiosResponse<Page<TeleSignLogResponse>>>('/telesign', {
      params: {
        ...params,
        fromDate: params?.fromDate
          ? dayjs(params.fromDate).format('YYYY-MM-DD')
          : null,
        toDate: params?.toDate
          ? dayjs(params.toDate).format('YYYY-MM-DD')
          : null,
      },
      signal,
    })
    .then((response) => response.data);
}

export async function startTeleSign(
  request: StartTeleSignRequest,
  signal?: AbortSignal,
): Promise<Extract<TeleSignStatusResponse, { status: 'PENDING' }>> {
  return client
    .post<
      StartTeleSignRequest,
      AxiosResponse<Extract<TeleSignStatusResponse, { status: 'PENDING' }>>
    >('/telesign', request, { signal })
    .then((response) => response.data);
}

export async function cancelTeleSign(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete<void, AxiosResponse<void>>(`/telesign/${id}`, {
      signal,
    })
    .then((response) => response.data);
}

export async function getTeleSignStatus(
  id: string,
  signal?: AbortSignal,
): Promise<TeleSignStatusResponse> {
  return client
    .get<never, AxiosResponse<TeleSignStatusResponse>>(`/telesign/${id}`, {
      signal,
    })
    .then((response) => response.data);
}

export async function downloadTeleSignPdf(
  id: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/telesign/${id}/pdf`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function startTeleSignSigning(
  id: string,
  signal?: AbortSignal,
): Promise<Extract<TeleSignSigningStatusResponse, { status: 'PENDING' }>> {
  return client
    .post<
      never,
      AxiosResponse<
        Extract<TeleSignSigningStatusResponse, { status: 'PENDING' }>
      >
    >(`/telesign/${id}/sign`, { signal })
    .then((response) => response.data);
}

export async function getTeleSignSigningStatus(
  id: string,
  signal?: AbortSignal,
): Promise<TeleSignSigningStatusResponse> {
  return client
    .get<never, AxiosResponse<TeleSignSigningStatusResponse>>(
      `/telesign/${id}/sign`,
      { signal },
    )
    .then((response) => response.data);
}
